import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Breadcrumb, Card, Tabs } from 'antd';
import {
  ApartmentOutlined,
  CarOutlined,
  HomeOutlined,
  LockOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import SiteConfigBlocks from './siteConfigBlocks';
import SiteConfigVehicles from './siteConfigVehicles';
import SiteConfigResidents from './siteConfigResidents';
import SiteConfigDoors from './siteConfigDoors';
import SiteConfigAccountApplication from './siteConfigAccountApplication';
import SiteConfigSiteStaff from './siteConfigSiteStaff';
import SiteConfigDoorStaffCards from './siteConfigDoorStaffCards';
import SiteConfigDoorKeypadPins from './siteConfigDoorKeypadPins';
import SiteConfigDoorStatus from './siteConfigDoorStatus';
import SiteConfigPlateCameras from './siteConfigPlateCameras';
import SEO from './SEO';
import useAuthClaims from '../common/useAuthClaims';
import './style.scss';

// rev 124 (2026-05-19) — two-level nested tabs for SiteConfig.  The
// flat list grew to 10 tabs by rev 123.3, which made the tab bar
// visually crowded + scanning-unfriendly. We now group them into 4
// thematic top-level tabs (with icons) and put the original tab labels
// as sub-tabs underneath. Sub-tab names dropped the redundant "Door"
// prefix wherever the parent already conveys it.
//
// rev 102 claim gating preserved: a sub-tab carrying `requiredClaim`
// is hidden when the user doesn't have it. A group with NO visible
// sub-tabs (e.g., user without superAdmin viewing "People" if
// "Account Applications" weren't open to all) is hidden entirely.
//
// FRONTEND gating only — same caveats as before. The backend
// callables enforce their own claim checks.
const GROUPS = [
  {
    key: 'general',
    tab: <span><ApartmentOutlined /> General</span>,
    children: [
      { key: 'blocks',    tab: 'Blocks & Units' },
      { key: 'residents', tab: 'Residents Overview' },
    ],
  },
  {
    key: 'doors',
    tab: <span><LockOutlined /> Doors</span>,
    children: [
      { key: 'doors',          tab: 'Channels' },
      { key: 'doorStatus',     tab: 'Activity' },
      { key: 'doorStaffCards', tab: 'Staff RFID Cards', requiredClaim: 'superAdmin' },
      { key: 'doorKeypadPins', tab: 'Keypad PINs',      requiredClaim: 'superAdmin' },
    ],
  },
  {
    key: 'vehicles',
    tab: <span><CarOutlined /> Vehicles</span>,
    children: [
      { key: 'vehicles',     tab: 'Whitelist' },
      { key: 'plateCameras', tab: 'Plate Cameras', requiredClaim: 'superAdmin' },
    ],
  },
  {
    key: 'people',
    tab: <span><TeamOutlined /> People</span>,
    children: [
      { key: 'siteStaff',          tab: 'Site Staff',           requiredClaim: 'superAdmin' },
      { key: 'accountApplication', tab: 'Account Applications' },
    ],
  },
];

// D24 (2026-05-13): siteId is the Firestore doc id (UUID) for the assigned
// site, sourced from home.jsx via firGetUserAssignedSiteId. Previously this
// prop was named `siteName` and carried `undefined` because the home.jsx
// caller referenced an unbound variable — all site-config Firestore queries
// silently failed against the wrong path.
export default function SiteConfig({ siteId }) {
  const claims = useAuthClaims();

  // Filter sub-tabs against claims, then hide any group whose children
  // are all gated out. Root sees everything regardless.
  const visibleGroups = useMemo(() => GROUPS
    .map((g) => ({
      ...g,
      children: g.children.filter((c) => {
        if (!c.requiredClaim) return true;
        if (claims.root) return true;
        return Boolean(claims[c.requiredClaim]);
      }),
    }))
    .filter((g) => g.children.length > 0),
  [claims.root, claims.superAdmin, claims.admin]); // eslint-disable-line react-hooks/exhaustive-deps

  // rev 124.1 (2026-05-19) — URL is the source of truth for which
  // tab is active. `?tab=<groupKey>&sub=<subKey>` deep-links to a
  // specific sub-tab; bookmark/share/reload preserve the position;
  // browser back/forward works.
  //
  // Bare /siteConfig with no params falls back to the first visible
  // group + its first sub-tab. Invalid params (legacy bookmarks, a
  // tab the user lost claims for mid-session) silently snap to a
  // valid default — no error UI, no history pollution (no setSearchParams
  // write happens on auto-correction).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlGroupKey = searchParams.get('tab');
  const urlSubKey = searchParams.get('sub');

  const safeGroup = visibleGroups.find((g) => g.key === urlGroupKey)
    ?? visibleGroups[0];

  const safeSubKey = safeGroup
    ? (safeGroup.children.find((c) => c.key === urlSubKey)?.key
       ?? safeGroup.children[0]?.key)
    : null;

  // Writes are user-initiated only (tab clicks). preserveSearch keeps
  // any unrelated query params intact for future siblings.
  const writeUrl = (groupKey, subKey) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (groupKey) next.set('tab', groupKey); else next.delete('tab');
    if (subKey) next.set('sub', subKey); else next.delete('sub');
    return next;
  });

  const onGroupChange = (key) => {
    // Switching groups picks the group's first visible sub-tab. We
    // intentionally don't restore the per-group last-active sub-tab
    // any more — URL is the only state, and that simpler model
    // beats remembering across switches.
    const group = visibleGroups.find((g) => g.key === key);
    writeUrl(key, group?.children[0]?.key);
  };

  const onSubChange = (key) => {
    writeUrl(safeGroup?.key, key);
  };

  const contentMap = {
    blocks: <SiteConfigBlocks siteId={siteId} />,
    vehicles: <SiteConfigVehicles siteId={siteId} />,
    residents: <SiteConfigResidents siteId={siteId} />,
    doors: <SiteConfigDoors siteId={siteId} />,
    accountApplication: <SiteConfigAccountApplication siteId={siteId} />,
    siteStaff: <SiteConfigSiteStaff siteId={siteId} />,
    doorStaffCards: <SiteConfigDoorStaffCards siteId={siteId} />,
    doorKeypadPins: <SiteConfigDoorKeypadPins siteId={siteId} />,
    doorStatus: <SiteConfigDoorStatus siteId={siteId} />,
    plateCameras: <SiteConfigPlateCameras siteId={siteId} />,
  };

  // Sub-tabs rendered inside the parent Card content. Using antd
  // <Tabs> (not nested Card.tabList) so the visual hierarchy is
  // clear: top is bold underlined "category" tabs, inner is the
  // standard line of sub-tabs.
  const subTabItems = (safeGroup?.children || []).map((c) => ({
    key: c.key,
    label: c.tab,
    children: contentMap[c.key] || null,
  }));

  return (
    <>
      <SEO
        description="Manage site configuration"
        name="qiwei"
        title="Site Config"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            { title: <span>Site Config</span> },
          ]}
        />
      </div>
      <Card
        activeTabKey={safeGroup?.key}
        className="setting card"
        onTabChange={onGroupChange}
        tabList={visibleGroups.map((g) => ({ key: g.key, tab: g.tab }))}
      >
        <Tabs
          activeKey={safeSubKey}
          onChange={onSubChange}
          items={subTabItems}
          size="small"
          // Avoid the default top margin Card adds — we want sub-tabs
          // visually attached to the parent header, not floating in
          // mid-content.
          style={{ marginTop: -8 }}
          // destroyOnHidden so each sub-tab content unmounts when you
          // switch away. Matters for the live Door Activity tab — we
          // want the Firestore listener detached when the tab is hidden
          // instead of streaming in the background.
          destroyInactiveTabPane
        />
      </Card>
    </>
  );
}

SiteConfig.propTypes = {
  siteId: PropTypes.string.isRequired,
};
